import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import EpcProjectWizard, {
  type ExistingDoc,
  type WizardInitialValues,
  type WizardSubmitPayload,
} from '../project/create/EpcProjectWizard';
import epcProjectService, { type EpcProject } from '../../../services/epcProjectService';
import { fileService } from '../../../services/fileService';

function refId(ref: unknown): string | undefined {
  if (!ref) return undefined;
  if (typeof ref === 'string') return ref;
  if (typeof ref === 'object' && '_id' in (ref as object)) {
    const v = (ref as { _id?: unknown })._id;
    return v ? String(v) : undefined;
  }
  if (typeof ref === 'object' && 'id' in (ref as object)) {
    const v = (ref as { id?: unknown }).id;
    return v ? String(v) : undefined;
  }
  return undefined;
}

function refIds(refs: unknown[]): string[] {
  return refs.map((r) => refId(r) ?? '').filter(Boolean);
}

function attachmentName(displayName: string, originalName: string, tagLabel: string): string {
  const ext = originalName.includes('.') ? '.' + originalName.split('.').pop() : '';
  const base = displayName.endsWith(ext) ? displayName : `${displayName}${ext}`;
  return `[${tagLabel}] ${base}`;
}

export default function EpcProjectEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [project, setProject] = useState<EpcProject | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!id) {
      setLoading(false);
      return;
    }
    const load = async () => {
      setLoading(true);
      try {
        const proj = await epcProjectService.get(id);
        if (!cancelled) setProject(proj);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'Could not load project';
        toast.error(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [id]);

  const initialValues = useMemo<WizardInitialValues | undefined>(() => {
    if (!project) return undefined;
    const customerId = refId(project.customerId);
    const projectTypeId = refId(project.projectTypeId);
    const projectCategoryId = refId(project.projectCategoryId);
    const locationIds: string[] = [];
    if (Array.isArray(project.locationIds) && project.locationIds.length) {
      locationIds.push(...refIds(project.locationIds));
    } else if (project.locationId) {
      const single = refId(project.locationId);
      if (single) locationIds.push(single);
    }
    return {
      code: project.code,
      name: project.name,
      customerId,
      projectTypeId,
      projectCategoryId,
      locationIds,
      dcCapacityMw: project.dcCapacityMw,
      acCapacityMw: project.acCapacityMw,
      status: project.status,
      commissioningDate: project.commissioningDate,
      startDate: project.startDate,
      endDatePlanned: project.endDatePlanned,
      projectManager: project.projectManager,
      remarks: project.remarks,
    };
  }, [project]);

  const existingDocs = useMemo<ExistingDoc[]>(() => {
    if (!project?.epcAttachments) return [];
    return project.epcAttachments.map((a) => ({
      fileName: a.fileName,
      fileKey: a.fileKey,
      uploadedAt: a.uploadedAt,
    }));
  }, [project]);

  const handleSubmit = async (payload: WizardSubmitPayload) => {
    if (!id) return;
    setSubmitting(true);
    try {
      const newAttachments: { fileName: string; fileKey: string }[] = [];
      for (const att of payload.newAttachments) {
        const { key } = await fileService.uploadFile(att.file);
        newAttachments.push({
          fileName: attachmentName(att.displayName, att.file.name, att.tagLabel),
          fileKey: key,
        });
      }

      const finalAttachments = [
        ...payload.keptAttachments.map((k) => ({ fileName: k.fileName, fileKey: k.fileKey })),
        ...newAttachments,
      ];

      const body: Record<string, unknown> = {
        name: payload.name,
        code: payload.code,
        projectTypeId: payload.projectTypeId,
        // null persists a cleared category (backend also clears it when the type changes).
        projectCategoryId: payload.projectCategoryId ?? null,
        status: payload.status,
        customerId: payload.customerId ?? null,
        locationIds: payload.locationIds,
        dcCapacityMw: payload.dcCapacityMw ?? null,
        acCapacityMw: payload.acCapacityMw ?? null,
        commissioningDate: payload.commissioningDate ?? null,
        startDate: payload.startDate ?? null,
        endDatePlanned: payload.endDatePlanned ?? null,
        projectManager: payload.projectManager ?? null,
        remarks: payload.remarks ?? null,
        epcAttachments: finalAttachments,
      };

      await epcProjectService.update(id, body);
      toast.success('Project updated');
      navigate(`/epc/projects/${id}/overview`, { replace: true });
    } catch (err: unknown) {
      let msg = 'Could not update project';
      if (axios.isAxiosError(err)) {
        const d = err.response?.data as { message?: string; detail?: string } | undefined;
        msg = d?.detail || d?.message || err.message || msg;
      } else if (err instanceof Error) {
        msg = err.message;
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !project) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50/80">
        <div className="text-sm font-medium text-slate-500">
          {loading ? 'Loading project…' : 'Project not found'}
        </div>
      </div>
    );
  }

  return (
    <EpcProjectWizard
      mode="edit"
      title="Edit project"
      subtitle={`${project.code} — ${project.name}`}
      initialValues={initialValues}
      existingDocs={existingDocs}
      submitting={submitting}
      onSubmit={handleSubmit}
      onCancel={() => navigate(`/epc/projects/${id}/overview`)}
    />
  );
}
